import * as React from "react";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { FONT_BODY } from "../theme";
// Realistic vehicle inspection diagram (side · front/top/rear · side) used as a
// photographic backdrop; clickable hotspots are overlaid on top (Overlay-
// Methode). `?url` forces a plain asset URL import (no svgr component).
import carDiagram from "../../../assets/shutterstock_309121715.svg?url";

export interface CarZone {
  id: string;
  label: string;
}

// Granular, "standard"-style part breakdown (Stoßstange, Kotflügel, Türen,
// Seitenteile, Scheiben, Räder …) so the reporter can mark the exact panel.
export const CAR_ZONES: CarZone[] = [
  { id: "bumper_front", label: "Stoßstange vorne" },
  { id: "headlight_l", label: "Scheinwerfer links" },
  { id: "headlight_r", label: "Scheinwerfer rechts" },
  { id: "fender_fl", label: "Kotflügel vorne links" },
  { id: "fender_fr", label: "Kotflügel vorne rechts" },
  { id: "hood", label: "Motorhaube" },
  { id: "windshield", label: "Frontscheibe" },
  { id: "mirror_l", label: "Außenspiegel links" },
  { id: "mirror_r", label: "Außenspiegel rechts" },
  { id: "roof", label: "Dach" },
  { id: "door_fl", label: "Tür vorne links" },
  { id: "door_fr", label: "Tür vorne rechts" },
  { id: "door_rl", label: "Tür hinten links" },
  { id: "door_rr", label: "Tür hinten rechts" },
  { id: "quarter_rl", label: "Seitenteil hinten links" },
  { id: "quarter_rr", label: "Seitenteil hinten rechts" },
  { id: "rear_window", label: "Heckscheibe" },
  { id: "trunk", label: "Heckklappe / Kofferraum" },
  { id: "bumper_rear", label: "Stoßstange hinten" },
  { id: "wheel_fl", label: "Rad vorne links" },
  { id: "wheel_fr", label: "Rad vorne rechts" },
  { id: "wheel_rl", label: "Rad hinten links" },
  { id: "wheel_rr", label: "Rad hinten rechts" },
];

// Clickable region per part, as percentages of the diagram box (left/top/
// width/height). Mapped onto the 5 views: top side (left side of vehicle),
// front (centre-left), top-down (centre), rear (centre-right), bottom side
// (right side). Coordinates are intentionally generous tap targets and can be
// fine-tuned here in one place without touching the rest of the component.
type Hotspot = { l: number; t: number; w: number; h: number };
const HOTSPOTS: Record<string, Hotspot> = {
  // --- top side view: left side of the vehicle (front at left) ---
  fender_fl: { l: 17, t: 7, w: 10, h: 10 },
  wheel_fl: { l: 25, t: 14, w: 10, h: 13 },
  mirror_l: { l: 32, t: 3, w: 7, h: 5 },
  door_fl: { l: 35, t: 8, w: 10, h: 10 },
  door_rl: { l: 45, t: 8, w: 10, h: 10 },
  quarter_rl: { l: 55, t: 7, w: 11, h: 10 },
  wheel_rl: { l: 57, t: 14, w: 10, h: 13 },
  // --- front view (centre-left) ---
  headlight_l: { l: 2, t: 42, w: 7, h: 9 },
  headlight_r: { l: 12, t: 42, w: 7, h: 9 },
  bumper_front: { l: 2, t: 51, w: 17, h: 10 },
  // --- top-down view (centre): front at left → rear at right ---
  hood: { l: 19, t: 35, w: 15, h: 31 },
  windshield: { l: 34, t: 37, w: 9, h: 27 },
  roof: { l: 43, t: 39, w: 16, h: 23 },
  rear_window: { l: 59, t: 37, w: 8, h: 27 },
  trunk: { l: 67, t: 35, w: 12, h: 31 },
  // --- rear view (centre-right) ---
  bumper_rear: { l: 82, t: 50, w: 16, h: 11 },
  // --- bottom side view: right side of the vehicle ---
  fender_fr: { l: 17, t: 80, w: 10, h: 10 },
  wheel_fr: { l: 25, t: 75, w: 10, h: 13 },
  mirror_r: { l: 32, t: 89, w: 7, h: 5 },
  door_fr: { l: 35, t: 79, w: 10, h: 10 },
  door_rr: { l: 45, t: 79, w: 10, h: 10 },
  quarter_rr: { l: 55, t: 79, w: 11, h: 10 },
  wheel_rr: { l: 57, t: 75, w: 10, h: 13 },
};

interface Props {
  selected: string[];
  onToggle: (zoneId: string) => void;
}

const useStyles = makeStyles({
  wrap: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "14px",
  },
  frame: {
    position: "relative",
    width: "100%",
    maxWidth: "560px",
    aspectRatio: "1680 / 1095",
    backgroundColor: "#fff",
    ...shorthands.border("1px", "solid", "var(--sm-card-border)"),
    ...shorthands.borderRadius("16px"),
    ...shorthands.overflow("hidden"),
    boxShadow: "0 8px 22px rgba(0,0,0,0.05)",
    touchAction: "manipulation",
  },
  img: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "contain",
    pointerEvents: "none",
    userSelect: "none",
  },
  hotspot: {
    position: "absolute",
    appearance: "none",
    cursor: "pointer",
    backgroundColor: "transparent",
    ...shorthands.border("1.5px", "solid", "transparent"),
    ...shorthands.borderRadius("8px"),
    transitionProperty: "background-color, border-color",
    transitionDuration: "140ms",
    ":hover": {
      backgroundColor: "rgba(247,168,35,0.20)",
      ...shorthands.borderColor("var(--sm-accent)"),
    },
    ":focus-visible": {
      backgroundColor: "rgba(247,168,35,0.20)",
      ...shorthands.borderColor("var(--sm-accent)"),
      outlineStyle: "none",
    },
  },
  hotspotActive: {
    backgroundColor: "rgba(200,52,52,0.42)",
    ...shorthands.border("2px", "solid", "#c83434"),
    ":hover": {
      backgroundColor: "rgba(200,52,52,0.52)",
      ...shorthands.borderColor("#c83434"),
    },
  },
  legend: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
    justifyContent: "center",
    fontFamily: FONT_BODY,
  },
  pill: {
    backgroundColor: "var(--sm-accent-soft)",
    color: "var(--sm-primary-deep)",
    fontSize: "0.78rem",
    fontWeight: 600,
    paddingTop: "5px",
    paddingBottom: "5px",
    paddingLeft: "10px",
    paddingRight: "10px",
    ...shorthands.borderRadius("999px"),
    ...shorthands.border("1px", "solid", "var(--sm-accent)"),
  },
  hint: {
    color: "#6c6171",
    fontSize: "0.8rem",
    fontFamily: FONT_BODY,
    margin: 0,
    textAlign: "center",
  },
});

export default function CarSchematic({ selected, onToggle }: Props) {
  const styles = useStyles();
  const sel = (id: string) => selected.includes(id);
  const labelOf = React.useMemo(
    () => Object.fromEntries(CAR_ZONES.map((z) => [z.id, z.label])),
    [],
  );
  const labelsSelected = CAR_ZONES.filter((z) => sel(z.id));

  return (
    <div className={styles.wrap}>
      <div className={styles.frame}>
        <img className={styles.img} src={carDiagram} alt="Fahrzeug-Ansichten" />
        {CAR_ZONES.map((z) => {
          const h = HOTSPOTS[z.id];
          if (!h) return null;
          const active = sel(z.id);
          return (
            <button
              key={z.id}
              type="button"
              aria-label={labelOf[z.id]}
              aria-pressed={active}
              title={labelOf[z.id]}
              onClick={() => onToggle(z.id)}
              className={mergeClasses(styles.hotspot, active && styles.hotspotActive)}
              style={{
                left: `${h.l}%`,
                top: `${h.t}%`,
                width: `${h.w}%`,
                height: `${h.h}%`,
              }}
            />
          );
        })}
      </div>

      {labelsSelected.length === 0 ? (
        <p className={styles.hint}>
          Tippen Sie auf die betroffenen Teile des Fahrzeugs.
        </p>
      ) : (
        <div className={styles.legend} aria-live="polite">
          {labelsSelected.map((z) => (
            <span key={z.id} className={styles.pill}>
              {z.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
